import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, AlertTriangle, Info, CheckCircle, AlertCircle, Calendar, Heart, Lightbulb, ExternalLink, Trash2, BookMarked as MarkAsRead } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { format } from 'date-fns';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose }) => {
  const { 
    alerts, 
    unreadCount, 
    markAsRead, 
    clearAlert, 
    clearAllAlerts,
    sendBrowserNotification 
  } = useNotifications();
  
  const [filter, setFilter] = useState<'all' | 'unread' | 'critical'>('all');

  const getAlertIcon = (type: string, severity: string) => {
    if (severity === 'critical') return AlertCircle;
    if (severity === 'warning') return AlertTriangle;
    if (severity === 'success') return CheckCircle;
    
    switch (type) {
      case 'period_reminder': return Calendar;
      case 'ovulation_reminder': return Heart;
      case 'health_tip': return Lightbulb;
      default: return Info;
    }
  };

  const getAlertColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'from-red-500 to-pink-500';
      case 'warning': return 'from-yellow-500 to-orange-500';
      case 'success': return 'from-green-500 to-emerald-500';
      default: return 'from-blue-500 to-indigo-500';
    }
  };

  const getSeverityBadge = (severity: string) => {
    const badges = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critical' },
      warning: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Warning' },
      success: { bg: 'bg-green-100', text: 'text-green-800', label: 'Success' },
      info: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Info' }
    };
    
    return badges[severity as keyof typeof badges] || badges.info;
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'unread') return !alert.isRead;
    if (filter === 'critical') return alert.severity === 'critical';
    return true;
  });

  const handleMarkAsRead = (id: string) => {
    markAsRead(id);
  };

  const handleClearAlert = (id: string) => {
    clearAlert(id);
  };

  const handleTestNotification = () => {
    sendBrowserNotification(
      'Test Notification - THREEDOT',
      'This is a test notification to verify your browser notifications are working.',
      { icon: '🔔' }
    );
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden border border-white/20"
        initial={{ opacity: 0, scale: 0.9, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: -20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <motion.div
                className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg"
                whileHover={{ scale: 1.1, rotate: 10 }}
              >
                <Bell className="w-5 h-5 text-white" />
              </motion.div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
                {unreadCount > 0 && (
                  <p className="text-sm text-gray-600">{unreadCount} unread</p>
                )}
              </div>
            </div>
            <motion.button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="w-5 h-5" />
            </motion.button>
          </div>

          {/* Filter Tabs */}
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {[
              { key: 'all', label: 'All' },
              { key: 'unread', label: 'Unread' },
              { key: 'critical', label: 'Critical' }
            ].map((tab) => (
              <motion.button
                key={tab.key}
                onClick={() => setFilter(tab.key as any)}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  filter === tab.key
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-purple-600'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {tab.label}
                {tab.key === 'unread' && unreadCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                    {unreadCount}
                  </span>
                )}
              </motion.button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2 mt-4">
            <motion.button
              onClick={clearAllAlerts}
              className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-red-600 text-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear All</span>
            </motion.button>
            <motion.button
              onClick={handleTestNotification}
              className="flex items-center space-x-1 px-3 py-2 text-gray-600 hover:text-purple-600 text-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Bell className="w-4 h-4" />
              <span>Test</span>
            </motion.button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-96">
          <AnimatePresence>
            {filteredAlerts.length > 0 ? (
              filteredAlerts.map((alert, index) => {
                const IconComponent = getAlertIcon(alert.type, alert.severity);
                const severityBadge = getSeverityBadge(alert.severity);
                
                return (
                  <motion.div
                    key={alert.id}
                    className={`p-4 border-b border-gray-100 hover:bg-purple-50/50 transition-all duration-200 ${
                      !alert.isRead ? 'bg-purple-50/30 border-l-4 border-l-purple-500' : ''
                    }`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="flex items-start space-x-3">
                      <motion.div
                        className={`p-2 bg-gradient-to-br ${getAlertColor(alert.severity)} rounded-lg flex-shrink-0`}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                      >
                        <IconComponent className="w-4 h-4 text-white" />
                      </motion.div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-medium text-gray-900 text-sm">{alert.title}</h4>
                          <div className="flex items-center space-x-1 ml-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${severityBadge.bg} ${severityBadge.text}`}>
                              {severityBadge.label}
                            </span>
                            {!alert.isRead && (
                              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-700 mb-2 leading-relaxed">{alert.message}</p>
                        
                        {alert.data?.recommendation && (
                          <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg mb-2">
                            <p className="text-xs text-blue-800">
                              <strong>Recommendation:</strong> {alert.data.recommendation}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {format(alert.createdAt, 'MMM d, h:mm a')}
                          </span>
                          
                          <div className="flex items-center space-x-2">
                            {!alert.isRead && (
                              <motion.button
                                onClick={() => handleMarkAsRead(alert.id)}
                                className="flex items-center space-x-1 px-2 py-1 text-purple-600 hover:text-purple-700 text-xs"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <MarkAsRead className="w-3 h-3" />
                                <span>Mark Read</span>
                              </motion.button>
                            )}
                            
                            <motion.button
                              onClick={() => handleClearAlert(alert.id)}
                              className="flex items-center space-x-1 px-2 py-1 text-gray-500 hover:text-red-600 text-xs"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <X className="w-3 h-3" />
                              <span>Clear</span>
                            </motion.button>
                          </div>
                        </div>
                        
                        {alert.actionRequired && (
                          <motion.div
                            className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                          >
                            <div className="flex items-center space-x-2">
                              <AlertTriangle className="w-4 h-4 text-yellow-600" />
                              <span className="text-xs font-medium text-yellow-800">Action Required</span>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })
            ) : (
              <motion.div
                className="p-8 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4"
                  whileHover={{ scale: 1.1, rotate: 10 }}
                >
                  <Bell className="w-8 h-8 text-purple-500" />
                </motion.div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {filter === 'all' ? 'No notifications' : 
                   filter === 'unread' ? 'No unread notifications' : 
                   'No critical notifications'}
                </h3>
                <p className="text-gray-500 text-sm">
                  {filter === 'all' ? 'You\'re all caught up!' : 
                   filter === 'unread' ? 'All notifications have been read.' : 
                   'No critical health alerts at this time.'}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {filteredAlerts.length} notification{filteredAlerts.length !== 1 ? 's' : ''}
            </span>
            <motion.button
              onClick={handleTestNotification}
              className="flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-xs hover:bg-purple-200 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ExternalLink className="w-3 h-3" />
              <span>Test Browser Notifications</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default NotificationCenter;